import { GridPosition, CombatStats } from '../types/Combat.types';
import { EliteModifierId } from '../data/eliteModifiers';

// === CALCULS DE COMBAT PURS (SANS DÉPENDANCE PHASER) ===
// Fonctions testables unitairement.

/** Distance Manhattan entre deux positions */
export function gridDistance(a: GridPosition, b: GridPosition): number {
  return Math.abs(a.col - b.col) + Math.abs(a.row - b.row);
}

/** Calcule les dégâts bruts d'une attaque */
export function calculateDamage(
  attackerAtk: number,
  defenderDef: number,
  multiplier: number = 1,
  attackBonus: number = 0,
  attackMalus: number = 0,
): number {
  const rawDamage = Math.max(1, (attackerAtk + attackBonus - attackMalus) - defenderDef);
  return Math.max(1, Math.round(rawDamage * multiplier));
}

/** Calcule les dégâts d'une AoO */
export function calculateAoODamage(attackerAtk: number, defenderDef: number): number {
  return Math.max(1, Math.round(attackerAtk * 0.75) - defenderDef);
}

/** Calcule le bonus ATK du passif Meute (zombie) */
export function calculatePackBonus(
  attackerId: string,
  targetPos: GridPosition,
  allEnemies: { id: string; enemyType?: string; isAlive: boolean; position: GridPosition }[],
): number {
  return allEnemies.filter(e =>
    e.isAlive && e.id !== attackerId && e.enemyType === 'zombie'
    && gridDistance(e.position, targetPos) === 1
  ).length;
}

/** Calcule le bonus ATK du passif Flair du sang (requin) */
export function calculateBloodFrenzBonus(targetHp: number, targetMaxHp: number): number {
  return targetHp < targetMaxHp * 0.5 ? 2 : 0;
}

/** Vérifie si un mouvement déclenche des AoO */
export function getAoOThreats(
  oldPos: GridPosition,
  newPos: GridPosition,
  opponents: { isAlive: boolean; position: GridPosition }[],
): number {
  return opponents.filter(o =>
    o.isAlive
    && gridDistance(oldPos, o.position) === 1
    && gridDistance(newPos, o.position) > 1
  ).length;
}

/** Vérifie si un crabe bloque le mouvement (Étreinte) */
export function isCrabBlocking(
  moverPos: GridPosition,
  targetPos: GridPosition,
  enemies: { isAlive: boolean; enemyType?: string; position: GridPosition }[],
): boolean {
  return enemies.some(e =>
    e.isAlive && e.enemyType === 'crab'
    && gridDistance(moverPos, e.position) === 1
    && gridDistance(targetPos, e.position) > 1
  );
}

/** Clamp un heal pour ne pas dépasser maxHP */
export function clampHeal(currentHp: number, healAmount: number, maxHp: number): number {
  return Math.min(currentHp + healAmount, maxHp);
}

/** Calcule le heal de repos (30% du maxHP) */
export function calculateRestHeal(currentHp: number, maxHp: number): number {
  const heal = Math.floor(maxHp * 0.3);
  return clampHeal(currentHp, heal, maxHp);
}

/** Applique des dégâts, clamp à 0 minimum */
export function applyDamage(currentHp: number, damage: number): number {
  return Math.max(0, currentHp - damage);
}

/** Calcule le score d'expédition d'un survivant */
export function calculateExpeditionScore(stats: CombatStats): number {
  return stats.maxHp + stats.attack * 2 + stats.defense * 2;
}

// === MODIFICATEURS D'ÉLITE ===
// Fonctions pures correspondant aux hooks appliqués dans CombatScene.performAttack.

/** Bonus ATK dû au modificateur Enragé (actif si HP < 30% du max). */
export function applyEnragedBonus(
  attackerHp: number,
  attackerMaxHp: number,
  modifier: EliteModifierId | null | undefined,
): number {
  if (modifier !== 'enraged') return 0;
  return attackerHp < attackerMaxHp * 0.3 ? 2 : 0;
}

/**
 * Applique les modificateurs du défenseur aux dégâts bruts :
 * - `shield_phase` : ×0.5 si HP > 50% du max
 * - `resistant`    : −1 (minimum 1)
 * Ordre d'application identique à CombatScene.performAttack.
 */
export function applyDefenderModifiers(
  baseDamage: number,
  defenderHp: number,
  defenderMaxHp: number,
  modifier: EliteModifierId | null | undefined,
): number {
  let dmg = baseDamage;
  if (modifier === 'shield_phase' && defenderHp > defenderMaxHp * 0.5) {
    dmg = Math.max(1, Math.round(dmg * 0.5));
  }
  if (modifier === 'resistant') {
    dmg = Math.max(1, dmg - 1);
  }
  return dmg;
}

/**
 * Dégâts d'épines renvoyés à l'attaquant en mêlée.
 * Renvoie 0 si le modificateur n'est pas Épines ou si l'attaque n'est pas mêlée.
 */
export function calculateThornsDamage(
  defenderModifier: EliteModifierId | null | undefined,
  skillRange: number,
): number {
  if (defenderModifier !== 'thorns') return 0;
  // skill.range <= 1 OU pas de skill (attaque ennemie = mêlée par défaut)
  return skillRange <= 1 ? 1 : 0;
}

/**
 * Gère la résurrection : si le défenseur vient de descendre à 0 HP avec le
 * modificateur `revive`, il repart à 50% du maxHp et le modificateur est consommé.
 * Retourne les nouveaux HP + le nouveau modifier (null si consommé).
 */
export function applyReviveHook(
  hp: number,
  maxHp: number,
  modifier: EliteModifierId | null | undefined,
): { hp: number; modifier: EliteModifierId | null } {
  if (hp <= 0 && modifier === 'revive') {
    return {
      hp: Math.max(1, Math.round(maxHp * 0.5)),
      modifier: null,
    };
  }
  return { hp, modifier: modifier ?? null };
}

/** Heal de régénération (+2 HP/tour) pour le modificateur `regen`. */
export function calculateRegenHeal(
  currentHp: number,
  maxHp: number,
  modifier: EliteModifierId | null | undefined,
): number {
  if (modifier !== 'regen') return 0;
  return Math.min(2, Math.max(0, maxHp - currentHp));
}

/** Calcule le risque final d'une expédition */
export function calculateExpeditionRisk(
  baseRiskMin: number,
  baseRiskMax: number,
  teamScore: number,
  scoreThreshold: number,
): number {
  if (baseRiskMax === 0) return 0;
  const baseRisk = (baseRiskMin + baseRiskMax) / 2;
  const reduction = Math.min(1, teamScore / scoreThreshold) * 0.7;
  const finalRisk = baseRisk * (1 - reduction);
  return Math.max(baseRiskMin * 0.3, finalRisk);
}
